/* eslint-disable max-lines */
import { AgGridAngular } from '@ag-grid-community/angular';
import {
  CellClassParams,
  Column,
  FirstDataRenderedEvent,
  GetQuickFilterTextParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  RowHeightParams,
  RowNode,
  ValueFormatterParams,
} from '@ag-grid-community/core';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, ReplaySubject } from 'rxjs';
import { map, startWith, switchMap, tap } from 'rxjs/operators';

import { InputComponent } from '@shared/components/input/input.component';
import {
  ConfirmationActionModalComponent,
  ConfirmationActionModalData,
} from '@shared/components/modals/confirmation-action-modal/confirmation-action-modal.components';
import { VendorDropdownComponent } from '@shared/components/vendor-dropdown/vendor-dropdown.component';
import { NoopValueAccessorDirective } from '@shared/directives/noop-value-accessor.directive';
import { ApiService } from '@shared/services/api.service';
import {
  BudgetType,
  EventType,
  listUserNamesWithEmailQuery,
  User,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { AuthQuery } from '@shared/store/auth/auth.query';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { OrganizationService } from '@shared/store/organization/organization.service';
import {
  OrganizationModel,
  OrganizationStore,
} from '@shared/store/organization/organization.store';
import { Utils } from '@shared/utils/utils';

import { BudgetLibraryActionsComponent } from './budget-library-actions.component';
import { BudgetLibraryQuery } from './state/budget-library.query';
import { BudgetLibraryService } from './state/budget-library.service';
import { BudgetLibraryModel } from './state/budget-library.store';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    InputComponent,
    FormsModule,
    NoopValueAccessorDirective,
    VendorDropdownComponent,
    ReactiveFormsModule,
    AgGridAngular,
    AsyncPipe,
  ],
  selector: 'aux-budget-library',
  styles: [
    `
      ::ng-deep .budget-library-table .ag-cell.vendor-value-cell {
        border-width: 0 !important;
        justify-items: end;
        width: 300px !important;
        justify-content: left;
      }

      ::ng-deep .budget-library-table,
      .ag-row:not(.ag-row-first),
      .ag-cell.vendor-value-cell,
      .text-sm {
        padding: 25px 0 16px;
      }

      ::ng-deep .budget-library-table .ag-cell {
        color: var(--aux-black);
      }

      ::ng-deep .budget-library-table .ag-cell.vendor-cell {
        border-width: 0 !important;
        justify-items: end;
        width: 100px;
      }

      ::ng-deep .budget-library-table .ag-cell.grid-cell {
        border-top-width: 0 !important;
        border-left-width: 0 !important;
        border-right-width: 0 !important;
        border-bottom-width: 1px !important;
        border-color: var(--aux-gray-dark) !important;
      }

      ::ng-deep .budget-library-table .ag-header {
        border: 1px solid var(--aux-gray-dark) !important;
        border-radius: 0 0 0 0 !important;
        border-left-width: 0 !important;
      }

      ::ng-deep .budget-library-table .ag-header-row,
      ::ng-deep .budget-library-table .ag-header-cell {
        overflow: unset;
      }

      ::ng-deep .budget-library-table .ag-header-cell-text,
      ::ng-deep .budget-library-table .ag-row {
        font-size: 1rem;
      }

      ::ng-deep .budget-library-table .ag-header-cell {
        text-align: center;
        border-left: 1px solid var(--aux-gray-dark) !important;
      }

      ::ng-deep .budget-library-table .ag-header-cell-label {
        justify-content: center;
      }
    `,
  ],
  templateUrl: './budget-library.component.html',
})
export class BudgetLibraryComponent implements OnInit {
  public budgetLibraryQuery = inject(BudgetLibraryQuery);
  public organizationQuery = inject(OrganizationQuery);

  private apiService = inject(ApiService);
  private authQuery = inject(AuthQuery);
  private budgetLibraryService = inject(BudgetLibraryService);
  private readonly destroyRef = inject(DestroyRef);
  private eventService = inject(EventService);
  private mainQuery = inject(MainQuery);
  private organizationStore = inject(OrganizationStore);
  private overlayService = inject(OverlayService);
  private vendorsService = inject(OrganizationService);

  gridAPI?: GridApi;
  gridAPI$ = new ReplaySubject<GridApi>(1);
  gridData$!: Observable<BudgetLibraryModel[]>;
  gridOptions$ = new BehaviorSubject({
    autoGroupColumnDef: {
      cellRenderer: BudgetLibraryActionsComponent,
      cellRendererParams: {
        deleteClickFN: ({ rowNode }: { rowNode: RowNode }) => {
          this.budgetLibraryService.removeBudgetVersion(rowNode.data).catch(console.error);
        },
        downloadClickFN: ({ rowNode }: { rowNode: RowNode }) => {
          this.downloadBudgetLibrary(rowNode).catch(console.error);
        },
        hideRadioButton: true,
      },
      editable: false,
      field: 'actions',
      headerName: '',
      maxWidth: 90,
      minWidth: 80,
      suppressSizeToFit: true,
    },
    columnDefs: [
      {
        field: 'vendor_id',
        headerName: 'Vendor Contract',
        hide: true,
        rowGroup: true,
        valueFormatter: (params: ValueFormatterParams) => {
          return this.organizationQuery.getEntity(params.value)?.name || '';
        },
      },
      {
        field: 'vendor_name',
        headerName: 'Vendor Contract',
        hide: true,
      },
      {
        cellClass: (x: CellClassParams) => {
          if (!x?.data) {
            return 'vendor-cell';
          }

          return ['justify-center', 'grid-cell'];
        },
        cellRenderer: BudgetLibraryActionsComponent,
        cellRendererParams: {
          hideDeleteButton: true,
          hideDownloadButton: true,
        },
        editable: false,
        field: 'is_current',
        headerName: 'Current (LRE)',
        maxWidth: 80,
        width: 80,
      },
      {
        cellClass: (x: CellClassParams) => {
          if (!x?.data) {
            return 'vendor-cell';
          }

          return ['justify-center', 'grid-cell'];
        },
        cellRenderer: BudgetLibraryActionsComponent,
        cellRendererParams: {
          baselineClickFN: ({ rowNode }: { rowNode: RowNode }) => {
            if (!rowNode.data.is_baseline) {
              this.changeBudgetTypeBaseline(rowNode).catch(console.error);
            }
          },
          hideDeleteButton: true,
          hideDownloadButton: true,
          hideIsCurrent: true,
        },
        editable: false,
        field: 'is_baseline',
        headerName: 'Baseline',
        maxWidth: 80,
        width: 80,
      },
      {
        cellClass: (x: CellClassParams) => {
          if (!x?.data) {
            return 'vendor-cell ag-cell-align-left';
          }

          return ['justify-start', 'grid-cell', 'ag-cell-align-left'];
        },
        field: 'budget_name',
        headerName: 'Budget Name',
        maxWidth: 350,
        minWidth: 300,
        tooltipField: 'budget_name',
        valueFormatter: (x) => {
          if (x.value) {
            if (x.data.is_change_order) {
              return Utils.replaceChangeOrderFormat(x.value);
            }

            return x.value;
          }

          return '';
        },
      },
      {
        cellClass: (x: CellClassParams) => {
          if (!x?.data) {
            return 'vendor-cell';
          }

          return ['justify-start', 'grid-cell'];
        },
        field: 'budget_type',
        headerName: 'Budget Type',
        maxWidth: 250,
        minWidth: 220,
        valueFormatter: (x) => {
          if (x.data?.bucket_key === '' && !x.data?.is_change_order) {
            return 'In-Application Budget Creation';
          }

          return this.getFormattedBudgetTypeStatus(
            x.value,
            x.data === undefined ? false : x.data.is_change_order
          );
        },
      },
      {
        cellClass: (x: CellClassParams) => {
          if (!x?.data) {
            return 'vendor-cell';
          }

          return ['justify-end', 'grid-cell'];
        },
        field: 'total_budget_amount',
        headerName: 'Total',
        maxWidth: 250,

        minWidth: 200,
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.data && params.value) {
            return Utils.currencyFormatter(
              Number(params.value),
              {},
              this.organizationQuery.getEntity(params.data.vendor_id)?.currency
            );
          } else {
            return '';
          }
        },
      },
      {
        cellClass: (x: CellClassParams) => {
          if (!x?.data) {
            return 'vendor-cell';
          }

          return ['justify-end', 'grid-cell'];
        },
        field: 'create_date',
        getQuickFilterText: Utils.agDateFormatter,
        headerName: 'Date Created',
        maxWidth: 100,
        minWidth: 100,
        type: 'date',
        valueFormatter: (x) => {
          if (x.value) {
            return Utils.agDateFormatter(x);
          }

          return '';
        },
      },
      {
        cellClass: (x: CellClassParams) => {
          if (!x?.data) {
            return 'vendor-cell';
          }

          return ['grid-cell'];
        },
        field: 'created_by',
        getQuickFilterText: (params: GetQuickFilterTextParams) => this.userFormatter(params.value),
        headerName: 'Created by',
        maxWidth: 250,
        minWidth: 200,
        valueFormatter: (x) => {
          if (x.value) {
            return this.userFormatter(x.value);
          }

          return '';
        },
      },
    ],
    columnTypes: Utils.columnTypes,
    defaultColDef: {
      cellClass: (x: CellClassParams) => {
        if (!x?.data) {
          if (x.value) {
            return 'vendor-value-cell';
          }

          return 'vendor-cell';
        }

        return 'grid-cell';
      },
      minWidth: 80,
      resizable: false,
      sortable: false,
      suppressMenu: true,
      suppressMovable: true,
    },
    groupDefaultExpanded: -1,
    suppressCellFocus: true,
  } as GridOptions);
  nameFilterValue = '';
  selectedVendor = new UntypedFormControl('');
  users = new Map<string, Pick<User, 'email' | 'family_name' | 'given_name'>>();
  vendors: (OrganizationModel & Required<Pick<OrganizationModel, 'name'>>)[] = [];
  width$ = new BehaviorSubject(0);

  ngOnInit() {
    combineLatest([
      this.eventService.select$(EventType.TRIAL_CHANGED),
      this.mainQuery.select('trialKey'),
    ])
      .pipe(
        startWith(null),
        switchMap(() => {
          return combineLatest([
            this.budgetLibraryService.get(),
            this.vendorsService.get().pipe(
              switchMap(() => {
                return this.budgetLibraryQuery.selectAll().pipe(
                  tap((budgets) => {
                    const vendorsWithBudgets = budgets.map((budget) => budget.vendor_id);
                    const allVendors = this.organizationQuery.getAllVendors();

                    this.vendors = allVendors
                      .filter((vendor) => vendorsWithBudgets.includes(vendor.id))
                      .map((item) => ({ ...item, name: item.name || '' }));

                    if (this.vendors.length === 1) {
                      this.organizationStore.setActive(this.vendors[0].id);
                      this.selectedVendor.setValue(this.vendors[0].id);
                    } else {
                      // reset any older selected vendors.
                      this.organizationStore.setActive(null);
                      this.selectedVendor.setValue('');
                    }
                  })
                );
              })
            ),
          ]);
        })
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.gridData$ = this.budgetLibraryQuery.selectAll().pipe(
          switchMap((listData) => {
            return this.selectedVendor.valueChanges.pipe(
              startWith(this.selectedVendor.value as string),
              map((selected_organization) => {
                const org = this.organizationQuery.getEntity(selected_organization);

                if (selected_organization) {
                  return listData.filter((budget) => {
                    return (budget.vendor_name as string) === (org?.name as string);
                  });
                }

                return listData.sort((x, y) => {
                  let comp = 0;

                  if (x.vendor_name === y.vendor_name) {
                    if (
                      x.budget_type === BudgetType.BUDGET_PRIMARY &&
                      (x.is_current ||
                        (x.is_baseline &&
                          (!y.is_current || y.budget_type !== BudgetType.BUDGET_PRIMARY)))
                    ) {
                      comp = -1;
                    }
                  } else {
                    comp =
                      y.vendor_name && x.vendor_name ? (x.vendor_name > y.vendor_name ? 1 : -1) : 0;
                  }

                  return comp;
                });
              })
            );
          })
        );
      });
  }

  autoSize() {
    this.gridAPI?.sizeColumnsToFit();
  }

  async changeBudgetTypeBaseline(rowNode: RowNode) {
    const { budget_version_id, vendor_id } = rowNode.data;

    if (!budget_version_id) {
      this.overlayService.error("This bugdet doesn't have a version.");

      return;
    }

    const modalEvent = this.overlayService.openPopup<
      ConfirmationActionModalData,
      boolean,
      ConfirmationActionModalComponent
    >({
      data: {
        keywordToExecuteAction: 'Replace Baseline',
        message:
          'This will replace the budget version used as baseline budget and have an  impact on budget variances.',
      },
      modal: ConfirmationActionModalComponent,
      settings: {
        header: 'Replace Budget Baseline?',
        primaryButton: { label: 'Replace Baseline' },
      },
    }).afterClosed$;

    const resp = await firstValueFrom(modalEvent);

    if (resp?.data) {
      await this.budgetLibraryService.updateBudgetVersionAsBaseline(budget_version_id, vendor_id);
    } else {
      this.budgetLibraryService.updateLastBudgetVersion(rowNode.data);
    }
  }

  constructor() {
    this.mainQuery
      .select('userList')
      .pipe(takeUntilDestroyed())
      .subscribe((_users) => {
        _users.forEach((user: listUserNamesWithEmailQuery) => {
          this.users.set(user.sub, user);
        });
      });
  }

  async downloadBudgetLibrary(rowNode: RowNode) {
    const { bucket_key } = rowNode.data;

    if (!bucket_key) {
      this.overlayService.error("This budget version doesn't have file");

      return;
    }

    const ref = this.overlayService.loading();
    const { data, success } = await this.apiService.getS3ZipFile(bucket_key);

    if (success && data) {
      const fileName =
        `${rowNode.data.budget_name}_${rowNode.data.vendor_name}_Budget_Library_` +
        `${rowNode.data.budget_id}_${(rowNode.data.create_date || '').slice(0, 10)}`;

      await this.apiService.downloadZipOrFile(data, fileName);
    }

    ref.close();
  }

  getFormattedBudgetTypeStatus(type: BudgetType, isChangeOrder: boolean): string {
    const mapBudgetStatus = new Map<BudgetType, string>([
      [BudgetType.BUDGET_CHANGE_ORDER, 'Change Order'],
      [BudgetType.BUDGET_MODEL, 'Model'],
      [BudgetType.BUDGET_PRIMARY, isChangeOrder ? 'Change Order' : 'Budget Upload'],
      [BudgetType.BUDGET_SECONDARY, 'Scenario Budget'],
      [BudgetType.BUDGET_VENDOR_ESTIMATE, 'Vendor Estimate'],
    ]);

    return mapBudgetStatus.get(type) || '';
  }

  getRowHeight(params: RowHeightParams): null | number {
    return params.node.rowIndex !== 0 && params.node.group ? 60 : null;
  }

  onDataRendered(e: FirstDataRenderedEvent) {
    this.gridAPI = e.api;
    this.gridAPI$.next(e.api);
    const allColumnIds: string[] = [];

    this.gridAPI.getColumns()?.forEach((column: Column) => {
      allColumnIds.push(column.getColId());
    });
    this.gridAPI.autoSizeColumns(allColumnIds, false);
    this.gridAPI.sizeColumnsToFit();

    this.refreshGridWidth();
  }

  onGridReady({ api }: GridReadyEvent) {
    api.sizeColumnsToFit();
    this.refreshGridWidth();
  }

  refreshGridWidth() {
    const div = document.querySelector('.ag-header-container') as HTMLDivElement;

    if (div) {
      const paddingOffset = 2;

      this.width$.next(div.getBoundingClientRect().width + paddingOffset);
    } else {
      this.width$.next(0);
    }
  }

  userFormatter(sub: string | undefined) {
    const user = this.users.get(sub || '');

    if (user) {
      const isUserAuxAdmin = user.email.includes('@auxili.us');

      if (this.authQuery.isAuxAdmin() || !isUserAuxAdmin) {
        return `${user.given_name} ${user.family_name}`;
      }

      return 'Auxilius Expert';
    }

    return Utils.zeroHyphen;
  }
}
